import { Component } from "@theme/component";
import { onAnimationEnd } from "@theme/utilities";
import { ThemeEvents, CartUpdateEvent } from "@theme/events";
import { safeSessionStorage } from "@theme/safe-storage";

/**
 * Route `sessionStorage` access through `safeSessionStorage` so the cart icon
 * mounts cleanly when storage throws `SecurityError` (Safari "Block All
 * Cookies", sandboxed iframes without `allow-same-origin`, partitioned
 * third-party contexts, etc.). Optimistic cart-count rehydration is silently
 * skipped when storage is unavailable.
 * Also skip `cart:update` when `itemCount` is omitted (morph-only Peak Saver
 * refreshes) — a missing count coerced to 0 would clear the badge.
 * Related: assets/safe-storage.js, GH issue #487, Sentry SHOPIFY-THEME-29
 */

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLButtonElement} [cartButton] - The cart button. Its label carries the count.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ["cartBubble", "cartBubbleText", "cartBubbleCount"];

  /**
   * Bubble text is blank at 100 or more. The button label still ends with the
   * real count, which is what a product-form add has to increment.
   * @type {number}
   */
  get currentCartCount() {
    const displayed = parseInt(this.refs.cartBubbleCount.textContent ?? "", 10);
    if (Number.isFinite(displayed)) return displayed;

    const label = this.refs.cartButton?.getAttribute("aria-label") ?? "";
    const labeled = parseInt(label.match(/(\d+)\s*$/)?.[1] ?? "", 10);
    return Number.isFinite(labeled) ? labeled : 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : "";
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    // Morph-only updates (e.g. Peak Saver section refresh) may omit itemCount.
    // Do not coerce missing → 0 or the badge clears and session storage stores 0.
    const itemCount = event.detail.data?.itemCount;
    if (typeof itemCount !== "number") return;

    const comingFromProductForm =
      event.detail.data?.source === "product-form-component";

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (
    itemCount,
    comingFromProductForm,
    animate = true,
  ) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubble.hidden = itemCount === 0;
    this.refs.cartBubbleCount.hidden = true;
    this.refs.cartBubble.classList.toggle(
      "cart-bubble--animating",
      itemCount > 0 && animate,
    );

    const count = comingFromProductForm
      ? this.currentCartCount + itemCount
      : itemCount;
    this.currentCartCount = count;

    const { cartButton } = this.refs;
    if (cartButton?.dataset.cartLabel && Number.isFinite(count)) {
      cartButton.ariaLabel = `${cartButton.dataset.cartLabel}: ${count}`;
    }

    this.classList.toggle("header-actions__cart-icon--has-cart", itemCount > 0);

    safeSessionStorage.setItem(
      "cart-count",
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      }),
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove("cart-bubble--animating");
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = safeSessionStorage.getItem("cart-count");
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null)
      return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get("cart-icon")) {
  customElements.define("cart-icon", CartIcon);
}
